/**
 * Shared helpers for the general-purpose file attachment upload flow
 * (Projects / Quotes / Products). Mirrors the validation rules enforced by
 * internal/server/files_controller.go so the client rejects bad files before
 * ever requesting a signed URL.
 */
import {
  ALLOWED_ATTACHMENT_EXTENSIONS,
  ALLOWED_ATTACHMENT_EXTENSIONS_PATTERN,
  MAX_FILE_ATTACHMENT_BYTES,
  type AllowedAttachmentExtension,
} from '../constants/app';
import { formatAttachmentBytes } from './emailAttachments';

/** Extension -> content-type. Must match fileAttachmentContentTypes in files_controller.go (server 400s on mismatch). */
const EXTENSION_CONTENT_TYPES: Record<AllowedAttachmentExtension, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.txt': 'text/plain',
};

export const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return '';
  }
  return fileName.slice(lastDotIndex).toLowerCase();
};

const isAllowedAttachmentExtension = (extension: string): extension is AllowedAttachmentExtension =>
  (ALLOWED_ATTACHMENT_EXTENSIONS as readonly string[]).includes(extension);

export const contentTypeForFileName = (fileName: string): string | null => {
  const extension = getFileExtension(fileName);
  if (!isAllowedAttachmentExtension(extension)) {
    return null;
  }
  return EXTENSION_CONTENT_TYPES[extension];
};

/**
 * Validates a file against the same rules the backend enforces (extension,
 * size, non-empty) before any network request is made. Returns a
 * user-facing error message, or null when the file is acceptable.
 *
 * `maxBytes` defaults to the per-file upload cap; the email flow passes the
 * budget still available under MAX_ATTACHMENT_BYTES instead.
 */
export const validateAttachmentFile = (
  file: { name: string; size: number },
  maxBytes: number = MAX_FILE_ATTACHMENT_BYTES,
): string | null => {
  if (file.size === 0) {
    return "This file appears to be empty and can't be attached.";
  }

  if (!ALLOWED_ATTACHMENT_EXTENSIONS_PATTERN.test(file.name)) {
    return 'Only PDF, Word, Excel, image (JPG/PNG), and text files are supported.';
  }

  if (file.size > maxBytes) {
    return `This file is too large. The maximum size is ${formatAttachmentBytes(Math.max(0, maxBytes))}.`;
  }

  return null;
};
